import { useEffect } from "react";
import { motion } from "framer-motion";
import glowImage from "../assets/optimized/glow.webp";
import { useLocation } from "react-router-dom";
import Mission from "../assets/optimized/mission.webp";
import ImageScroller from "../components/imageScroller";
import Services from "../components/services";
import { Helmet } from "react-helmet";
import HeroSection from "../components/HeroSection";
import BeliefCard from "../components/BeliefCard";
import BeliefImage from "../assets/optimized/beliefImage.webp";

export default function Home() {
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo) {
      const el = document.getElementById(location.state.scrollTo);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 75,
    },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.15,
        duration: 0.6,
      },
    }),
  };

  return (
    <>
      <Helmet>
        <title>Pure Business Solutions | Coaching, Planning & Support</title>
        <meta
          name="description"
          content="Pure Business Solutions empowers individuals and organizations through coaching, event planning, training, and virtual assistance."
        />
        <meta
          name="keywords"
          content="Life Coaching, Event Planning, Business Training, Virtual Assistance, Pure Business Solutions, PBS"
        />
        <meta name="author" content="Pure Business Solutions" />
        <meta property="og:title" content="Pure Business Solutions" />
        <meta
          property="og:description"
          content="Navigating life's challenges with strategy and support through coaching and planning services."
        />
        <meta property="og:image" content="https://www.pbizs.com/logo512.png" />
        <meta property="og:url" content="https://www.pbizs.com" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Pure Business Solutions" />
        <meta
          name="twitter:description"
          content="Empowering lives through coaching, planning, training, and support."
        />
        <link rel="icon" href="/favicon.png" />
      </Helmet>

      <HeroSection />
      <section
        id="missionStatement"
        className="bg-[#111111] py-24 px-6 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="flex justify-center">
              <div className="relative">
                {/* Gold Glow */}
                <div className="absolute -inset-4 bg-[#C8A46B]/20 blur-2xl rounded-[32px]" />

                <img
                  src={Mission}
                  alt="Maurice & Kim Praying"
                  className="
                    relative
                    w-full
                    max-w-xl
                    rounded-[32px]
                    object-cover
                    shadow-[0_20px_60px_rgba(0,0,0,0.5)]
                  "
                />
              </div>
            </div>

            {/* Content */}
            <div>
              <p className="uppercase tracking-[4px] text-[#C8A46B] font-semibold mb-4">
                Our Purpose
              </p>

              <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-8">
                Our Mission
              </h2>

              <div className="w-24 h-1 bg-[#D63384] rounded-full mb-8" />

              <p className="text-lg text-gray-300 leading-relaxed">
                Our mission is to help couples strengthen their relationships
                through faith, communication, practical tools, biblical wisdom,
                and intentional heart work.
              </p>

              <p className="text-lg text-gray-300 leading-relaxed mt-6">
                We are committed to helping couples move from simply surviving
                to truly flourishing in love, unity, and purpose while building
                Christ-centered relationships that endure life's challenges.
              </p>

              <div className="mt-10">
                <button
                  className="
                    bg-[#C8A46B]
                    hover:bg-[#B08C57]
                    text-white
                    px-8
                    py-4
                    rounded-full
                    shadow-lg
                    transition-all
                    duration-300
                    hover:-translate-y-1
                  "
                >
                  Discover Our Approach
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we believe Section */}
      <section id="beliefs" className="bg-[#E7DCF3] py-28 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Top Section */}
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* Content */}
            <motion.div
              initial={{
                opacity: 0,
                x: -75,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{
                duration: 0.8,
              }}
            >
              <p className="uppercase tracking-[5px] text-[#A89A87] font-semibold mb-4">
                Our Core Beliefs
              </p>

              <h2 className="text-5xl lg:text-6xl font-bold text-[#2C2C2C] leading-tight">
                What We Believe
              </h2>

              <h3 className="text-4xl lg:text-5xl font-light italic text-[#CDBBA7] mt-2">
                About Marriage
              </h3>

              <div className="w-24 h-[2px] bg-[#CDBBA7] mt-8 mb-8" />

              <p className="text-lg text-gray-700 leading-relaxed">
                We believe marriage should not be entered into lightly. Marriage
                is a sacred covenant designed by God, built on love, commitment,
                sacrifice, communication, forgiveness, and unity.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mt-6">
                We believe every couple can grow when they are willing to do the
                heart work required to build a healthy relationship. With proper
                guidance, practical resources, and a willingness to apply God's
                Word, marriages can be restored, strengthened, and renewed.
              </p>
            </motion.div>

            {/* Image */}
            <motion.div
              className="flex justify-center"
              initial={{
                opacity: 0,
                x: 75,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{
                duration: 1,
              }}
            >
              <img
                src={BeliefImage}
                alt="Couple praying together"
                className="
                  w-full
                  max-w-xl
                  rounded-[36px]
                  shadow-[0_25px_60px_rgba(0,0,0,0.12)]
                "
              />
            </motion.div>
          </div>

          {/* Belief Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mt-24">
            <motion.div
              custom={0}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <BeliefCard
                icon="✝️"
                text="Marriage is a covenant between husband, wife, and God."
              />
            </motion.div>

            <motion.div
              custom={1}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <BeliefCard
                icon="💬"
                text="Marriage requires intentionality, communication, forgiveness, and grace."
              />
            </motion.div>

            <motion.div
              custom={2}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <BeliefCard
                icon="📖"
                text="Strong relationships are built through biblical truth and practical application."
              />
            </motion.div>

            <motion.div
              custom={3}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <BeliefCard
                icon="🤝"
                text="Couples do not have to struggle alone."
              />
            </motion.div>

            <motion.div
              custom={4}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <BeliefCard
                icon="❤️"
                text="With the right tools and guidance, marriages can flourish."
              />
            </motion.div>
          </div>
        </div>
      </section>
      <section className="bg-white py-28 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <p className="uppercase tracking-[5px] text-[#D4AF37] font-semibold mb-4">
              Why Work With Us
            </p>

            <h2 className="text-5xl lg:text-6xl font-bold text-[#2C2C2C]">
              Why Couples
              <br />
              Choose Maurice & Kim
            </h2>

            <div className="w-24 h-[2px] bg-[#D4AF37] mx-auto my-8"></div>

            <p className="text-lg text-gray-600 leading-relaxed">
              Couples choose Maurice and Kim because they combine biblical truth
              with real-life experience. After more than 27 years of marriage
              and over a decade of coaching couples, they understand that
              lasting relationships require grace, intentionality, practical
              tools, and a strong faith-centered foundation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-20">
            <div className="bg-[#F8F3ED] rounded-[30px] p-10 text-center">
              <h3 className="text-5xl font-bold text-[#D4AF37]">27+</h3>
              <p className="mt-4 text-lg text-[#D4AF37] font-semibold">Years Married</p>
            </div>

            <div className="bg-[#F8F3ED] rounded-[30px] p-10 text-center">
              <h3 className="text-5xl font-bold text-[#D4AF37]">10+</h3>
              <p className="mt-4 text-lg text-[#D4AF37] font-semibold">
                Years Coaching Couples
              </p>
            </div>

            <div className="bg-[#F8F3ED] rounded-[30px] p-10 text-center">
              <h3 className="text-5xl font-bold text-[#D4AF37]">🇺🇸🌍</h3>
              <p className="mt-4 text-lg text-[#D4AF37]  font-semibold">
                Clients in the U.S. & Abroad
              </p>
            </div>

            <div className="bg-[#F8F3ED] rounded-[30px] p-10 text-center">
              <h3 className="text-5xl font-bold text-[#D4AF37]">❤️</h3>
              <p className="mt-4 text-lg text-[#D4AF37] font-semibold">Faith-Based Coaching</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-20">
            <div className="space-y-5 text-lg text-gray-700">
              <div>✔ In-Person & Virtual Appointments</div>

              <div>✔ Flexible Scheduling</div>

              <div>✔ Practical, Compassionate Coaching</div>

              <div>✔ Transparent Biblical Guidance</div>
            </div>

            <div className="space-y-5 text-lg text-gray-700">
              <div>✔ Special Pricing for Military</div>

              <div>✔ Student Discounts</div>

              <div>✔ Senior Discounts</div>

              <div>✔ Annual Marriage Retreats & Conferences</div>
            </div>
          </div>
        </div>
      </section>
      <div className="w-full min-h-screen text-accentyellow font-sans">
        {/* Scroller and Services Sections */}

        <section
          id="services"
          className="relative overflow-hidden bg-[#F9F5EF]"
        >
          {/* Background Images */}
          <ImageScroller background />

          {/* Cream Overlay */}
          <div className="absolute inset-0 bg-[#F9F5EF]/85 backdrop-blur-sm" />

          {/* Everything above the background */}
          <div className="relative z-10">
            <Services />

            <div className="w-full flex justify-center pb-20">
              <div className="relative bg-white text-center px-8 py-6 max-w-3xl rounded-3xl shadow-xl border border-[#D4AF37]/30">
                <h2 className="text-xl sm:text-2xl font-semibold text-[#2A2A2A]">
                  🎟️ We proudly offer special pricing for
                </h2>

                <p className="mt-2 text-lg font-bold text-[#D4AF37]">
                  Veterans • Senior Citizens • College Students
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className="max-w-6xl mx-auto my-20 rounded-[40px] bg-[#EAF6FF] py-24 px-6 shadow-lg">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C2C2C] leading-tight">
              Your Story Isn't Over.
            </h2>

            <div className="w-28 h-1 bg-[#D4AF37] mx-auto mt-6 mb-10 rounded-full"></div>

            <p className="text-xl md:text-2xl leading-relaxed text-[#555] max-w-4xl mx-auto">
              Marriage is a covenant—not a casual commitment. It is a sacred
              partnership between husband, wife, and God. No matter where your
              journey has brought you, healing is possible, hope can be
              restored, and love can grow deeper than ever before.
            </p>

            <p className="text-xl md:text-2xl leading-relaxed text-[#555] max-w-4xl mx-auto mt-8">
              Through biblical guidance, practical tools, and compassionate
              coaching, we believe every relationship has the opportunity to
              flourish when hearts are willing and God remains at the center.
            </p>

            <p className="mt-12 text-3xl md:text-4xl font-semibold text-[#2C2C2C] italic leading-relaxed">
              <span className="text-[#D4AF37]">
                Your marriage can flourish.
              </span>
              <br />
              <span className="text-[#D63384]">
                Your relationship can grow.
              </span>
              <br />
              Your love can be strengthened—God's way.
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
